import { createWithEqualityFn } from 'zustand/traditional';
import { fetchUserSelf, type UserSelfData } from '../api/userSelf';

const calcDollar = (quota?: number | null): number => {
  const q = typeof quota === 'number' && Number.isFinite(quota) ? quota : 0;
  return q / 500000;
};

const formatDollar = (quota?: number | null): string => {
  const usd = calcDollar(quota);
  return `$${usd.toFixed(2)}`;
};

export interface UserSelfState {
  loading: boolean;
  error: string | null;
  data: UserSelfData | null;
  /** 形如 $12.34 的余额显示串（quota/500000，保留 2 位小数） */
  dollarLabel: string;
  lastUpdatedAt: number | null;
  refresh: () => Promise<void>;
}

export const useUserSelfStore = createWithEqualityFn<UserSelfState>()((set, get) => ({
  loading: false,
  error: null,
  data: null,
  dollarLabel: '$0.00',
  lastUpdatedAt: null,

  async refresh() {
    // 若正在刷新，避免并发
    if (get().loading) return;
    set({ loading: true, error: null });
    try {
      const resp = await fetchUserSelf();
      if (!resp?.success || !resp.data) {
        throw new Error(resp?.message || '获取用户信息失败');
      }
      const dollarLabel = formatDollar(resp.data.quota);
      set({
        data: resp.data,
        dollarLabel,
        loading: false,
        error: null,
        lastUpdatedAt: Date.now(),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : '获取用户信息异常';
      set({ loading: false, error: message, lastUpdatedAt: Date.now() });
    }
  },
}));

export const refreshUserSelf = async (): Promise<void> => {
  try {
    await useUserSelfStore.getState().refresh();
  } catch {
    // 静默失败，避免打断业务流程
  }
};

